import { Routes } from '@angular/router';
import { PathEnum, RoutesEnum } from './shared/enums/path-enum';
import { NoAuthGuard } from './guard/noAuth.guard';
import { AuthGuard } from './guard/auth.guard';
import { ModalGuard } from './guard/modal.guard';
import { nonOperatoreGuard, operatoreGuard, operatoreLoggatoGuard } from './guard/operatore.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: PathEnum.LOGIN,
    pathMatch: 'full'
  },
  {
    // `nonOperatoreGuard`: tiene un operatore fuori dalla shell del cittadino, rimandandolo alla
    // propria area. Senza, un operatore che arrivasse qui (link salvato, catch-all, rientro
    // automatico dopo un errore) monterebbe il menu cittadino e la discovery mensa, prenderebbe
    // 403 e finirebbe sull'onboarding: è la difformità B-01. Nessun effetto sui cittadini.
    // Anche in `canMatch`: `canActivate` da solo scarica prima il chunk della shell.
    canActivate: [AuthGuard, nonOperatoreGuard],
    canMatch: [nonOperatoreGuard],
    path: 'pages',
    loadChildren: () => import('./pages/pages.module').then(m => m.PagesPageModule)
  },
  {
    canActivate: [NoAuthGuard],
    canDeactivate: [ModalGuard],
    path: RoutesEnum.LOGIN,
    title: 'Accedi',
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginPageModule)
  },
  {
    canActivate: [NoAuthGuard],
    path: RoutesEnum.REGISTRAZIONE,
    title: 'Registrati',
    loadComponent: () =>
      import('./pages/registrazione/registrazione.page').then(m => m.RegistrazionePage)
  },
  {
    canDeactivate: [ModalGuard],
    path: RoutesEnum.RECOVER_PASSWORD,
    title: 'Recupera password',
    loadChildren: () => import('./pages/verify-account/verify-account.module').then(m => m.VerifyAccountPageModule)
  },
  {
    canDeactivate: [ModalGuard],
    path: 'verify-account',
    title: 'Verifica account',
    loadChildren: () => import('./pages/verify-account/verify-account.module').then(m => m.VerifyAccountPageModule)
  },
  {
    canDeactivate: [ModalGuard],
    path: 'enable-account',
    title: 'Abilita account',
    loadChildren: () => import('./pages/enable-account/enable-account.module').then(m => m.EnableAccountPageModule)
  },
  {
    path: 'esito-positivo',
    title: 'Esito positivo',
    loadChildren: () => import('./pages/redirect-ok/redirect-ok.module').then(m => m.RedirectOkPageModule)
  },
  {
    path: 'esito-negativo',
    title: 'Esito negativo',
    loadChildren: () => import('./pages/redirect-ko/redirect-ko.module').then(m => m.RedirectKoPageModule)
  },
  {
    path: 'auth/callback',
    title: 'Autenticazione in corso',
    loadChildren: () => import('./pages/auth-callback/auth-callback.module').then(m => m.AuthCallbackPageModule)
  },
  {
    path: 'auth/error',
    title: 'Errore di autenticazione',
    loadChildren: () => import('./pages/auth-error/auth-error.module').then(m => m.AuthErrorPageModule)
  },
  {
    path: 'error',
    redirectTo: 'auth/error',
  },
  {
    canActivate: [AuthGuard],
    path: RoutesEnum.ONBOARDING_PROFILO,
    title: 'Completa il profilo',
    loadChildren: () =>
      import('./pages/onboarding/completa-profilo/completa-profilo.module').then(
        (m) => m.CompletaProfiloPageModule
      ),
  },
  {
    canActivate: [AuthGuard],
    path: RoutesEnum.ONBOARDING_COMUNE,
    title: 'Scegli il tuo Comune',
    loadChildren: () =>
      import('./pages/comuni/comuni.module').then((m) => m.ComuniPageModule),
  },
  {
    // Rotta rimossa: i termini del cittadino sono ora i «documenti del Comune», accettati nella
    // modale dopo l'accesso. Il redirect serve ai segnalibri e alle web app in cache.
    path: 'termini-e-condizioni',
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    // ---- AREA OPERATORE di backoffice --------------------------------------------------------
    // Sezione TOP-LEVEL, deliberatamente FUORI da `/pages`: la shell del cittadino monta il menu,
    // l'header e la discovery mensa (`StudentiMensaService`), che interroga endpoint
    // `/cittadino/*` su cui un operatore prende 403. Finché l'area stava sotto `/pages`, quel 403
    // lo faceva espellere verso la home, che senza comune lo rimbalzava sull'onboarding del
    // cittadino: un ciclo infinito (difformità B-01 del collaudo). Separare le due shell toglie
    // la causa, invece di rincorrere i singoli endpoint.
    //
    // `operatoreGuard` protegge tutto tranne il login: verifica il ruolo nel token e rimanda un
    // cittadino alla sua area. Il backend resta autoritativo (lista filtrata per capability,
    // submit chiuso per authority): questo guard evita percorsi senza uscita, non difende il dato.
    path: RoutesEnum.OPERATORE,
    children: [
      {
        // Login dedicato: pubblico per forza di cose (ci si arriva senza sessione). `NoAuthGuard`
        // NON è riusabile qui — rimanda a `/pages/home`, cioè proprio la shell del cittadino da
        // cui l'operatore deve restare fuori. Chi è già autenticato come operatore viene portato
        // alla lista da `operatoreLoggatoGuard`.
        canActivate: [operatoreLoggatoGuard],
        canDeactivate: [ModalGuard],
        path: RoutesEnum.OPERATORE_LOGIN,
        title: 'Accesso operatori',
        loadComponent: () =>
          import('./features/operatore/login/operatore-login.page').then(
            (m) => m.OperatoreLoginPage
          ),
      },
      {
        // Elenco dei bandi su cui l'operatore può compilare: è la home dell'area.
        canActivate: [operatoreGuard],
        canMatch: [operatoreGuard],
        canDeactivate: [ModalGuard],
        path: '',
        title: 'Bandi disponibili',
        loadComponent: () =>
          import('./features/operatore/bandi/operatore-bandi.page').then(
            (m) => m.OperatoreBandiPage
          ),
      },
      {
        // Compilazione per conto del cittadino: monta la STESSA `CittadinoPraticaSubmitPage` del
        // cittadino nella terza modalità (`operatoreSubmitMode`), compilabile e sottomettibile.
        // `:idTp` è l'UUID del bando, mai il codice parlante: l'endpoint di submit risolve il
        // bando solo dall'id (difesa multi-tenant) e non fa fallback sul codice.
        canActivate: [operatoreGuard],
        canDeactivate: [ModalGuard],
        path: RoutesEnum.PRATICHE + '/nuova/:slug/:idTp',
        title: 'Nuova domanda per un cittadino',
        loadComponent: () =>
          import('./features/cittadino/pratiche/submit/cittadino-pratica-submit.page').then(
            (m) => m.CittadinoPraticaSubmitPage
          ),
        data: { operatoreSubmitMode: true },
      },
    ],
  },
  {
    // Anteprima pratica read-only PUBBLICA: nessun AuthGuard. Il ramo `/pages` è protetto dal
    // guard padre, quindi una figlia sotto `/pages` non potrebbe restare pubblica: la rotta è
    // registrata top-level. Monta la stessa `CittadinoPraticaSubmitPage` in `previewMode`.
    // DEVE precedere il catch-all `**`.
    path: 'anteprima-pratica/:slug/:idTp',
    title: 'Anteprima domanda',
    loadComponent: () =>
      import('./features/cittadino/pratiche/submit/cittadino-pratica-submit.page').then(
        (m) => m.CittadinoPraticaSubmitPage
      ),
    data: { previewMode: true },
  },
  {
    path: '**',
    redirectTo: PathEnum.PAGES + PathEnum.HOME,
  },
];
